// Client-side ingest for /api/track-event. Uses sendBeacon when
// available (survives page unload), falls back to fetch keepalive.
// Session id is per-tab, lives only in sessionStorage so it doesn't
// leak across browser sessions or windows.

const SESSION_STORAGE_KEY = 'lm-metrics-session';

function getSessionId(): string {
  try {
    let id = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (id) return id;
    id = randomId();
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, id);
    return id;
  } catch {
    // sessionStorage unavailable (private mode in some browsers etc.)
    // — return a fresh per-call id; coarser stats but never crashes.
    return randomId();
  }
}

function randomId(): string {
  const a = new Uint8Array(8);
  crypto.getRandomValues(a);
  return Array.from(a, (b) => b.toString(16).padStart(2, '0')).join('');
}

function getReferrerHost(): string | null {
  try {
    const ref = document.referrer;
    if (!ref) return null;
    const u = new URL(ref);
    if (u.host === window.location.host) return null;
    return u.host;
  } catch {
    return null;
  }
}

interface EventPayload {
  event_name:
    | 'pageview'
    | 'section-view'
    | 'section-dwell'
    | 'quiz-started'
    | 'quiz-completed';
  session_id: string;
  section_key?: string;
  pathname?: string;
  referrer_host?: string | null;
  duration_ms?: number;
  payload?: Record<string, unknown>;
}

/** Public shape of the structured payload we send on quiz-completed.
 *  Stored in metrics_events.payload (JSONB). Anonymous — derived from
 *  the user's quiz answers + the public candidate roster. */
export interface QuizCompletionPayload {
  district: number | null;
  compass: { x: number; y: number };
  topMatches: Array<{ id: number; score: number }>;
  bottomMatches: Array<{ id: number; score: number }>;
  partyAverages: Record<string, number>;
  questionsAnswered: number;
  totalQuestions: number;
  answers: Record<string, number>;
}

function send(payload: EventPayload): void {
  if (typeof window === 'undefined') return;
  const body = JSON.stringify(payload);
  const url = '/api/track-event';
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
      return;
    }
  } catch {
    /* fall through to fetch */
  }
  void fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => {
    /* fail silent — analytics never breaks the page */
  });
}

export function trackPageview(pathname: string): void {
  send({
    event_name: 'pageview',
    session_id: getSessionId(),
    pathname,
    referrer_host: getReferrerHost(),
  });
}

export function trackSectionView(sectionKey: string): void {
  send({
    event_name: 'section-view',
    session_id: getSessionId(),
    section_key: sectionKey,
  });
}

/** Section dwell time — fired when a section that was in view leaves
 *  the viewport (or the tab becomes hidden / unloads while it's still
 *  in view). Caller passes the elapsed ms; we cap at 60 000 ms so a
 *  single dwell on a tab left open all day doesn't dominate the
 *  averages. */
export function trackSectionDwell(sectionKey: string, durationMs: number): void {
  if (!Number.isFinite(durationMs) || durationMs < 250) return;
  const capped = Math.min(Math.round(durationMs), 60_000);
  send({
    event_name: 'section-dwell',
    session_id: getSessionId(),
    section_key: sectionKey,
    duration_ms: capped,
  });
}

/** Fired the first time a visitor clicks "Start" on the Quiz intro. The
 *  Quiz module guards re-firing via a flag in its persisted state, so
 *  this counts unique starts per browser, not per refresh. */
export function trackQuizStarted(): void {
  send({ event_name: 'quiz-started', session_id: getSessionId() });
}

/** Fired the first time a visitor reaches the results screen (step ≥ 1).
 *  Same per-browser dedupe via the Quiz module's persisted state.
 *  Carries the full structured result (district, party scores, top /
 *  bottom matches, compass position, raw answers) so the admin
 *  dashboard can roll up real-world quiz outcomes — see
 *  QuizCompletionPayload. */
export function trackQuizCompleted(payload: QuizCompletionPayload): void {
  send({
    event_name: 'quiz-completed',
    session_id: getSessionId(),
    payload: payload as unknown as Record<string, unknown>,
  });
}
